import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Reveal, KineticLines } from "@/components/motion/Reveal";
import SmartImg from "@/components/SmartImg";

function SpecTable({ title, rows, loc, lang }) {
  return (
    <div>
      <h3 className="text-[11px] uppercase tracking-[0.25em] text-clara-accent font-semibold mb-4">{title}</h3>
      <table className="w-full text-sm tabular-nums border border-clara-text/15">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-clara-surface/40" : ""}>
              <td className="px-4 py-3 text-clara-text/60 border-b border-clara-text/10 w-1/2">{loc(r.label)}</td>
              <td className="px-4 py-3 text-clara-text font-medium border-b border-clara-text/10">
                {r.value_id && lang === "id" ? r.value_id : r.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CocoaGrade() {
  const { grade } = useParams();
  const navigate = useNavigate();
  const { loc, lang } = useLang();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["cocoa-grade", grade],
    queryFn: async () => (await api.get(`/cocoa-grades/${grade}`)).data,
  });

  if (isLoading) return <div className="bg-clara-bg min-h-screen flex items-center justify-center text-clara-text/60">Loading…</div>;
  if (isError || !data) return <div className="bg-clara-bg min-h-screen flex items-center justify-center text-clara-text/60">Grade not found.</div>;

  const g = data.grade;

  return (
    <div className="bg-clara-bg text-clara-text min-h-screen" data-testid={`grade-page-${grade}`}>
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24">
        <button onClick={() => navigate("/brands/clara")} data-testid="grade-back-btn" className="inline-flex items-center gap-2 text-sm text-clara-text/60 hover:text-clara-accent transition-colors mb-10">
          <ArrowLeft size={16} /> {loc({ id: "Kembali ke Clara", en: "Back to Clara" })}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: visual */}
          <div>
            <div className="relative aspect-square overflow-hidden bg-clara-surface">
              <SmartImg src={g.image_url} alt={g.grade} priority className="h-full w-full object-cover" />
              <div className={`absolute inset-0 mix-blend-multiply ${g.real_photo ? "opacity-0" : "opacity-70"}`} style={{ background: g.swatch }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
              <div className="absolute inset-0 flex items-end p-8">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-white/80">Clara Cocoa Powder</div>
                  <h1 className="font-serif text-7xl md:text-8xl text-white leading-none mt-2">
                    <KineticLines lines={[g.grade]} />
                  </h1>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 border border-clara-text/20 px-4 py-2 text-sm">
                <span className="h-3 w-3 rounded-full" style={{ background: g.swatch }} /> {loc(g.color)}
              </span>
              <span className="border border-clara-text/20 px-4 py-2 text-sm">pH {g.ph}</span>
            </div>
          </div>

          {/* Right: specs */}
          <div className="space-y-10">
            <Reveal>
              <SpecTable title={loc({ id: "Karakteristik Fisik", en: "Physical Characteristics" })} rows={g.physical} loc={loc} lang={lang} />
            </Reveal>
            <Reveal delay={0.1}>
              <SpecTable title={loc({ id: "Mikrobiologi", en: "Microbiological" })} rows={data.microbiological} loc={loc} lang={lang} />
            </Reveal>

            <p className="text-sm text-clara-text/50 italic">* Can be customized on request.</p>

            <div className="flex flex-wrap gap-3 pt-2">
              {g.tds_url ? (
                <a
                  href={g.tds_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="grade-download-btn"
                  className="inline-flex items-center gap-2 border border-clara-text/25 text-clara-text px-6 py-3.5 text-sm font-semibold uppercase tracking-wider hover:border-clara-accent hover:text-clara-accent transition-colors"
                >
                  <Download size={16} /> {loc({ id: "Unduh Spesifikasi", en: "Download Spec Sheet" })}
                </a>
              ) : (
                <button
                  data-testid="grade-download-btn"
                  onClick={() => toast.info(loc({ id: "Lembar spesifikasi PDF akan segera tersedia. Silakan hubungi kami untuk TDS.", en: "PDF spec sheet coming soon. Please contact us for the TDS." }))}
                  className="inline-flex items-center gap-2 border border-clara-text/25 text-clara-text px-6 py-3.5 text-sm font-semibold uppercase tracking-wider hover:border-clara-accent transition-colors"
                >
                  <Download size={16} /> {loc({ id: "Unduh Spesifikasi", en: "Download Spec Sheet" })}
                </button>
              )}
              <Link
                to="/contact?type=export"
                data-testid="grade-sample-btn"
                className="group inline-flex items-center gap-2 bg-clara text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-wider hover:bg-[#8A362B] transition-colors"
              >
                {loc({ id: "Minta Sampel", en: "Request a Sample" })} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
